import SysColumn from "app/class/sys-column";
import SysTable from "app/class/sys-table";
import { referenceFilterHasConditions } from "shared/services/referenceFilter";

export interface SysColumnOverride {
    id: string;
    sys_table_id: string;
    sys_column_id: string;
    active: boolean;
    label: string | null;
    is_mandatory: boolean | null;
    is_read_only: boolean | null;
    default_value: string | null;
    default_value_type?: string | null;
    default_value_script?: string | null;
    default_value_timeout_ms?: number | null;
    reference_filter: string | null;
    depends_on_column_id?: string | null;
}

export const aggregateInheritedColumns = (columns: SysColumn[], tables: SysTable[], overrides: SysColumnOverride[] = []) => {
    const tableByName = new Map(tables.map((table) => [table.name, table]));
    const ownByTableId = new Map<string, SysColumn[]>();
    columns.forEach((column) => {
        const current = ownByTableId.get(column.sys_table_id) || [];
        current.push(column);
        ownByTableId.set(column.sys_table_id, current);
    });
    const safeOverrides = Array.isArray(overrides) ? overrides : [];
    const overrideByTableAndColumn = new Map(safeOverrides.filter((item) => item?.active)
        .map((item) => [`${item.sys_table_id}:${item.sys_column_id}`, item]));

    const resolve = (table: SysTable, visiting = new Set<string>()): SysColumn[] => {
        if (visiting.has(table.name)) return ownByTableId.get(table.id) || [];

        const own = ownByTableId.get(table.id) || [];
        const parent = table.parent_table_name ? tableByName.get(table.parent_table_name) : undefined;
        if (!parent) return own;

        const nextVisiting = new Set(visiting).add(table.name);
        const inherited = resolve(parent, nextVisiting).map((column) => {
            const override = overrideByTableAndColumn.get(`${table.id}:${column.id}`);
            const values: Record<string, unknown> = { ...(column as any), sys_table_id: table.id };
            const overridden: Record<string, boolean> = {};
            if (override) {
                (["label", "is_mandatory", "is_read_only", "reference_filter", "depends_on_column_id"] as const)
                    .forEach((key) => {
                        if (override[key] !== null && override[key] !== undefined &&
                            (key !== "reference_filter" || referenceFilterHasConditions(override.reference_filter))) {
                            values[key] = override[key];
                            overridden[key] = true;
                        }
                    });
                if (override.default_value_type != null) {
                    values.default_value_type = override.default_value_type;
                    values.default_value = override.default_value_type === "literal" ? override.default_value ?? "" : "";
                    values.default_value_script = override.default_value_type === "script" ? override.default_value_script ?? "" : "";
                    values.default_value_timeout_ms = override.default_value_type === "script" ? override.default_value_timeout_ms : null;
                    overridden.default_value_type = true;
                    overridden.default_value = true;
                }
            }
            values.overridden = overridden;
            return new SysColumn(values as unknown as ConstructorParameters<typeof SysColumn>[0]);
        });
        const ownNames = new Set(own.map((column) => column.name));
        return [...inherited.filter((column) => !ownNames.has(column.name)), ...own];
    };

    return tables.flatMap((table) => resolve(table));
};
